import { DOCUMENT } from '@angular/common';
import { HttpBackend, HttpClient, HttpContextToken, HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { ApplicationRef, Component, ElementRef, EnvironmentInjector, Injectable, ViewChild, createComponent, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { catchError, firstValueFrom, from, switchMap, throwError, timeout } from 'rxjs';

/** Optional timeout for each HTTP attempt, excluding time spent entering account confirmation. */
export const FINANCIAL_REQUEST_TIMEOUT = new HttpContextToken<number>(() => 0);

export type FinancialChallenge = { code: string; purpose: string; binding: string; action: string };

@Component({
  selector: 'apex-financial-confirmation', standalone: true, imports: [FormsModule],
  template: `<dialog #dialog class="fc" aria-labelledby="financial-confirmation-title" aria-describedby="financial-confirmation-action" (cancel)="cancel($event)">
    <form class="fc__card" (ngSubmit)="confirm()">
      <div class="fc__badge" aria-hidden="true">
        <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <rect x="4" y="10" width="16" height="11" rx="2.5"></rect><path d="M8 10V7a4 4 0 0 1 8 0v3"></path><circle cx="12" cy="15.5" r="1.2"></circle>
        </svg>
      </div>
      <h2 id="financial-confirmation-title" class="fc__title">Confirm it’s you</h2>
      <p id="financial-confirmation-action" class="fc__action">{{ challenge.action }}</p>
      <p class="fc__hint">For your security, confirm your identity before this change is made.</p>
      @if (ready()) {
        <label class="fc__label" for="financial-proof">{{ method === 'AUTHENTICATOR' ? 'Authenticator code' : 'Password' }}</label>
        <div class="fc__field">
          <input id="financial-proof" name="proof" class="fc__input"
            [type]="method === 'AUTHENTICATOR' ? 'text' : (revealed() ? 'text' : 'password')"
            [attr.autocomplete]="method === 'AUTHENTICATOR' ? 'one-time-code' : 'current-password'"
            [attr.inputmode]="method === 'AUTHENTICATOR' ? 'numeric' : null"
            [attr.placeholder]="method === 'AUTHENTICATOR' ? '6-digit code' : 'Enter your password'"
            [attr.aria-invalid]="error() ? 'true' : null" [(ngModel)]="proof" [disabled]="busy()" required autofocus />
          @if (method !== 'AUTHENTICATOR') {
            <button type="button" class="fc__reveal" (click)="revealed.set(!revealed())" [disabled]="busy()"
              [attr.aria-label]="revealed() ? 'Hide password' : 'Show password'">{{ revealed() ? 'Hide' : 'Show' }}</button>
          }
        </div>
      } @else { <p class="fc__loading">Loading confirmation…</p> }
      @if (error()) { <p class="fc__error" role="alert">{{ error() }}</p> }
      <div class="fc__actions">
        <button type="button" class="fc__button fc__button--secondary" (click)="cancel()" [disabled]="busy()">Cancel</button>
        <button type="submit" class="fc__button fc__button--primary" [disabled]="!ready() || busy() || !proof.trim()">{{ busy() ? 'Confirming…' : 'Confirm' }}</button>
      </div>
    </form>
  </dialog>`,
  styles: [`
    :host {
      --fc-surface: var(--apex-surface, var(--p-content-background, #fff));
      --fc-text: var(--apex-text, var(--p-text-color, #0f172a));
      --fc-muted: var(--apex-text-muted, var(--p-text-muted-color, #64748b));
      --fc-border: var(--apex-border, var(--p-content-border-color, #e2e8f0));
      --fc-primary: var(--workspace-primary, var(--p-primary-color, #2fc1f1));
    }
    /* A global reset removes the browser's margin:auto, which left the dialog in the top-left corner. */
    .fc { position: fixed; inset: 0; margin: auto; height: fit-content; max-height: calc(100dvh - 32px);
      width: min(420px, calc(100vw - 32px)); padding: 0; border: 0; border-radius: 20px; overflow: auto;
      background: var(--fc-surface); color: var(--fc-text); box-shadow: 0 24px 70px rgba(15, 23, 42, .28); }
    .fc::backdrop { background: rgba(15, 23, 42, .55); backdrop-filter: blur(3px); }
    .fc[open] { animation: fc-in 160ms ease-out; }
    @keyframes fc-in { from { opacity: 0; transform: translateY(8px) scale(.98); } to { opacity: 1; transform: none; } }
    .fc__card { display: flex; flex-direction: column; padding: 28px 28px 24px; font-size: 14px; line-height: 1.5; }
    .fc__badge { display: grid; place-items: center; width: 48px; height: 48px; margin-bottom: 16px; border-radius: 14px;
      color: var(--fc-primary); background: color-mix(in srgb, var(--fc-primary) 14%, transparent); }
    .fc__title { margin: 0; font-size: 22px; font-weight: 800; line-height: 1.25; color: var(--fc-text); }
    .fc__action { margin: 6px 0 0; font-size: 15px; font-weight: 600; color: var(--fc-text); }
    .fc__hint { margin: 6px 0 20px; color: var(--fc-muted); }
    .fc__label { margin-bottom: 6px; font-size: 13px; font-weight: 700; color: var(--fc-text); }
    .fc__field { position: relative; }
    .fc__input { box-sizing: border-box; width: 100%; height: 46px; padding: 0 64px 0 14px; border-radius: 12px; font: inherit;
      color: var(--fc-text); background: var(--fc-surface); border: 1px solid var(--fc-border); outline: none;
      transition: border-color 160ms ease, box-shadow 160ms ease; }
    .fc__input:focus { border-color: var(--fc-primary); box-shadow: 0 0 0 4px color-mix(in srgb, var(--fc-primary) 22%, transparent); }
    .fc__input[aria-invalid=true] { border-color: #ef4444; }
    .fc__reveal { position: absolute; top: 50%; right: 8px; transform: translateY(-50%); height: 32px; padding: 0 10px; border: 0;
      border-radius: 8px; background: transparent; color: var(--fc-muted); font: inherit; font-size: 13px; font-weight: 700; cursor: pointer; }
    .fc__reveal:hover { color: var(--fc-primary); }
    .fc__loading { margin: 8px 0; color: var(--fc-muted); }
    .fc__error { margin: 10px 0 0; padding: 10px 12px; border-radius: 10px; color: #ef4444; background: rgba(239, 68, 68, .12); font-size: 13px; }
    .fc__actions { display: flex; justify-content: flex-end; gap: 12px; margin-top: 24px; }
    .fc__button { min-width: 120px; height: 44px; padding: 0 18px; border-radius: 14px; border: 1px solid transparent; font: inherit;
      font-size: 14px; font-weight: 800; cursor: pointer; transition: background-color 160ms ease, border-color 160ms ease, color 160ms ease; }
    .fc__button:disabled { opacity: .55; cursor: not-allowed; }
    .fc__button--primary { background: var(--fc-primary); color: #fff; }
    .fc__button--primary:not(:disabled):hover { background: color-mix(in srgb, var(--fc-primary) 88%, #000); }
    .fc__button--secondary { background: var(--fc-surface); color: var(--fc-muted); border-color: var(--fc-border); }
    .fc__button--secondary:not(:disabled):hover { color: var(--fc-primary); border-color: var(--fc-primary); }
    @media (max-width: 480px) { .fc__card { padding: 22px 18px 18px; } .fc__actions { flex-direction: column-reverse; } .fc__button { width: 100%; } }
  `],
})
export class FinancialConfirmationDialog {
  @ViewChild('dialog', { static: true }) dialog!: ElementRef<HTMLDialogElement>;
  private readonly http = new HttpClient(inject(HttpBackend));
  challenge!: FinancialChallenge;
  authUrl = '';
  finish: (token: string | null) => void = () => {};
  method = 'PASSWORD'; proof = '';
  readonly ready = signal(false); readonly busy = signal(false); readonly error = signal(''); readonly revealed = signal(false);
  async open(): Promise<void> {
    this.dialog.nativeElement.showModal();
    try {
      const response = await firstValueFrom(this.http.get<{ method: string }>(this.authUrl, { credentials: 'include' }));
      if (!['PASSWORD', 'AUTHENTICATOR'].includes(response.method)) throw new Error('Unknown confirmation method');
      this.method = response.method; this.ready.set(true);
    } catch { this.error.set('Confirmation is unavailable. Cancel and try again.'); }
  }
  async confirm(): Promise<void> {
    if (this.busy() || !this.ready() || !this.proof.trim()) return;
    this.busy.set(true); this.error.set('');
    try {
      const response = await firstValueFrom(this.http.post<{ token: string }>(this.authUrl, {
        purpose: 'FINANCIAL_ACTION', binding: this.challenge.binding,
        ...(this.method === 'AUTHENTICATOR' ? { code: this.proof } : { password: this.proof }),
      }, { credentials: 'include' }));
      if (!response.token) throw new Error('Missing confirmation');
      this.proof = ''; this.revealed.set(false); this.finish(response.token);
    } catch (error) {
      this.proof = '';
      this.error.set(error instanceof HttpErrorResponse && error.status === 429
        ? 'Too many attempts. Try again in 15 minutes.' : 'Confirmation failed. Check your password or authenticator code.');
    } finally { this.busy.set(false); }
  }
  cancel(event?: Event): void { event?.preventDefault(); if (!this.busy()) { this.proof = ''; this.finish(null); } }
}

@Injectable({ providedIn: 'root' })
export class FinancialConfirmationService {
  private readonly document = inject(DOCUMENT);
  private readonly app = inject(ApplicationRef);
  private readonly injector = inject(EnvironmentInjector);
  private active = false;
  confirm(challenge: FinancialChallenge, gateway: string): Promise<string | null> {
    if (this.active) return Promise.reject(new Error('Finish the current confirmation first.'));
    this.active = true;
    return new Promise(resolve => {
      const host = this.document.createElement('apex-financial-confirmation');
      this.document.body.append(host);
      const component = createComponent(FinancialConfirmationDialog, { environmentInjector: this.injector, hostElement: host });
      component.instance.challenge = challenge;
      component.instance.authUrl = gateway.replace(/\/$/, '') + '/api/v1/user/step-up';
      component.instance.finish = token => {
        this.app.detachView(component.hostView); component.destroy(); host.remove(); this.active = false; resolve(token);
      };
      this.app.attachView(component.hostView); component.changeDetectorRef.detectChanges();
      void component.instance.open();
    });
  }
}

/** A 428 is returned before side effects. Only that response permits one retry; arbitrary failures never do. */
export function financialConfirmationInterceptor(gateway: string): HttpInterceptorFn {
  return (request, next) => {
    const confirmation = inject(FinancialConfirmationService);
    const requestTimeout = request.context.get(FINANCIAL_REQUEST_TIMEOUT);
    const send = (attempt: typeof request) => requestTimeout > 0
      ? next(attempt).pipe(timeout(requestTimeout)) : next(attempt);
    return send(request).pipe(catchError((error: unknown) => {
      const challenge = error instanceof HttpErrorResponse ? error.error as FinancialChallenge : null;
      if (!(error instanceof HttpErrorResponse) || error.status !== 428 || !request.url.startsWith(gateway.replace(/\/$/, '') + '/')
        || request.headers.has('X-Financial-Confirmation')
        || challenge?.code !== 'FINANCIAL_CONFIRMATION_REQUIRED' || challenge.purpose !== 'FINANCIAL_ACTION'
        || !/^[0-9a-f]{64}$/.test(challenge.binding)) return throwError(() => error);
      return from(confirmation.confirm(challenge, gateway)).pipe(switchMap(token => token
        ? send(request.clone({ setHeaders: { 'X-Financial-Confirmation': token } }))
        : throwError(() => new HttpErrorResponse({ status: 428, error: { message: 'Confirmation cancelled. Nothing was submitted.' } }))));
    }));
  };
}
